import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { firstValueFrom } from 'rxjs';

import { CommonService } from 'src/app/core/services/common.services';

import { LegalDocumentType } from './legal-document-content.service';

type PdfRgb = [number, number, number];

interface PdfImageData {
  dataUrl: string;
  format: 'PNG' | 'JPEG';
}

export interface LegalDocumentPdfExportData {
  documentType: LegalDocumentType;
  title: string;
  lastUpdated: string;
  direction: PdfDirection;
  html: string;
}

interface PdfHeadingBlock { type: 'heading'; text: string; }
interface PdfParagraphBlock { type: 'paragraph'; text: string; emphasis: boolean; }
interface PdfListBlock { type: 'list'; items: string[]; }
interface PdfDividerBlock { type: 'divider'; }
type PdfBlock = PdfHeadingBlock | PdfParagraphBlock | PdfListBlock | PdfDividerBlock;
type PdfDirection = 'ltr' | 'rtl';
type PdfTextAlign = 'left' | 'right';

interface PdfRenderContext {
  direction: PdfDirection;
  contentBottom: number;
  contentLeft: number;
  contentRight: number;
  contentWidth: number;
  addPage: () => number;
}

interface PdfPageCountCapable {
  getNumberOfPages(): number;
}

interface PdfGStateCapable {
  GState(parameters: { opacity: number }): unknown;
  setGState(gState: unknown): jsPDF;
}

// Layout constants
const MARGIN_X    = 15;    // mm left/right margin
const HEADER_H    = 12;    // mm — same height as footer for visual symmetry
const TITLE_H     = 18;    // mm — centered title+date block (page 1 only)
const FOOTER_H    = 12;    // mm — footer bar
const BODY_COLOR: PdfRgb  = [55,  65,  81];
const TEXT_COLOR: PdfRgb  = [15,  23,  42];
const MUTED_COLOR: PdfRgb = [100, 116, 139];
const BRAND_COLOR: PdfRgb = [10,  33,  41];
const WHITE: PdfRgb        = [255, 255, 255];
const DIVIDER: PdfRgb      = [226, 232, 240];
const BRAND_LOGO_WIDTH = 22;
const BRAND_COLOR_CSS = 'rgb(10, 33, 41)';

@Injectable({ providedIn: 'root' })
export class LegalDocumentPdfService {

  constructor(private readonly _commonService: CommonService) {}

  // Public entry

  async exportLegalDocument(data: LegalDocumentPdfExportData): Promise<void> {
    if (!data.html?.trim()) throw new Error('Legal document HTML is required');

    const pdf   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const logo  = await this._loadLogo();
    const blocks = this._parseBlocks(data.html, data.title);

    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();

    // First page: header bar + title block
    const firstPageHeaderH = this._drawHeaderBar(pdf, data, logo, pw, /* firstPage */ true);
    this._drawTitleBlock(pdf, data, pw, firstPageHeaderH);

    // Content area starts below the title block
    let contentY = firstPageHeaderH + TITLE_H + 4;
    const contentBottom = ph - FOOTER_H - 4;
    const contentLeft   = MARGIN_X;
    const contentRight  = pw - MARGIN_X;
    const contentWidth  = contentRight - contentLeft;
    const renderContext: PdfRenderContext = {
      direction: data.direction,
      contentBottom,
      contentLeft,
      contentRight,
      contentWidth,
      addPage: () => {
        pdf.addPage();
        const nextPageHeaderH = this._drawHeaderBar(pdf, data, logo, pw, /* firstPage */ false);
        contentY = nextPageHeaderH + 6;
        return contentY;
      },
    };

    for (const block of blocks) {
      ({ y: contentY } = this._renderBlock(pdf, block, contentY, renderContext));
    }

    // Stamp footer on every page
    const total = this._getPageCount(pdf);
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      this._drawFooter(pdf, logo, p, total, pw, ph);
    }

    pdf.save(data.documentType === 'privacy' ? 'privacy-policy.pdf' : 'terms-and-conditions.pdf');
  }

  // Header bar (all pages)

  private _drawHeaderBar(
    pdf: jsPDF,
    data: LegalDocumentPdfExportData,
    logo: PdfImageData | null,
    pw: number,
    _firstPage: boolean,
  ): number {
    // Dark brand bar — same height as footer
    pdf.setFillColor(...BRAND_COLOR);
    pdf.rect(0, 0, pw, HEADER_H, 'F');

    this._drawBrandLogo(pdf, logo, 0, HEADER_H);

    this._drawHeaderBadge(pdf, data, pw);

    return HEADER_H;
  }

  // Centered title + date (page 1 only)

  private _drawTitleBlock(
    pdf: jsPDF,
    data: LegalDocumentPdfExportData,
    pw: number,
    headerHeight: number,
  ): void {
    const blockTop = headerHeight;
    const blockBot = blockTop + TITLE_H;

    // Hairline divider at top of block
    pdf.setDrawColor(...DIVIDER);
    pdf.setLineWidth(0.25);
    pdf.line(MARGIN_X, blockBot - 0.5, pw - MARGIN_X, blockBot - 0.5);

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(data.title, pw / 2, blockTop + 8.5, { align: 'center' });

    // Date
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(data.lastUpdated, pw / 2, blockTop + 14, { align: 'center' });
  }

  // Simple footer

  private _drawFooter(
    pdf: jsPDF,
    logo: PdfImageData | null,
    page: number,
    total: number,
    pw: number,
    ph: number,
  ): void {
    const footerY = ph - FOOTER_H;

    // Dark brand background - same as header
    pdf.setFillColor(...BRAND_COLOR);
    pdf.rect(0, footerY, pw, FOOTER_H, 'F');

    const centerY = footerY + FOOTER_H / 2 + 1;

    // Logo left
    this._drawBrandLogo(pdf, logo, footerY, FOOTER_H);

    // Page number right - white text
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(...WHITE);
    pdf.text(`${page} / ${total}`, pw - MARGIN_X, centerY, { align: 'right' });
  }

  // Block rendering

  private _renderBlock(
    pdf: jsPDF,
    block: PdfBlock,
    y: number,
    context: PdfRenderContext,
  ): { y: number } {
    switch (block.type) {
      case 'heading':
        return { y: this._renderHeadingBlock(pdf, block, y, context) };
      case 'paragraph':
        return { y: this._renderParagraphBlock(pdf, block, y, context) };
      case 'divider':
        return { y: this._renderDividerBlock(pdf, y, context) };
      case 'list':
        return { y: this._renderListBlock(pdf, block, y, context) };
    }
  }

  private _renderHeadingBlock(
    pdf: jsPDF,
    block: PdfHeadingBlock,
    y: number,
    context: PdfRenderContext,
  ): number {
    const fontSize = 10.5;
    const lineHeight = this._lineHeight(fontSize);
    const lines = this._splitLines(pdf, block.text, context.contentWidth, fontSize);
    const align = this._getTextAlign(context.direction);

    y = this._ensureSpace(y, lines.length * lineHeight + 4, context);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...BRAND_COLOR);

    for (const line of lines) {
      y = this._ensureSpace(y, lineHeight, context);
      pdf.text(line, this._getTextX(context), y, { align });
      y += lineHeight;
    }

    return y + 3;
  }

  private _renderParagraphBlock(
    pdf: jsPDF,
    block: PdfParagraphBlock,
    y: number,
    context: PdfRenderContext,
  ): number {
    const fontSize = 9;
    const lineHeight = this._lineHeight(fontSize);
    const lines = this._splitLines(pdf, block.text, context.contentWidth, fontSize);
    const align = this._getTextAlign(context.direction);

    pdf.setFont('helvetica', block.emphasis ? 'bold' : 'normal');
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...(block.emphasis ? TEXT_COLOR : BODY_COLOR));

    for (const line of lines) {
      y = this._ensureSpace(y, lineHeight + 0.5, context);
      pdf.text(line, this._getTextX(context), y, { align });
      y += lineHeight + 0.5;
    }

    return y + 2;
  }

  private _renderDividerBlock(pdf: jsPDF, y: number, context: PdfRenderContext): number {
    const nextY = this._ensureSpace(y, 5, context);
    pdf.setDrawColor(...DIVIDER);
    pdf.setLineWidth(0.2);
    pdf.line(context.contentLeft, nextY - 1, context.contentRight, nextY - 1);
    return nextY + 4;
  }

  private _renderListBlock(
    pdf: jsPDF,
    block: PdfListBlock,
    y: number,
    context: PdfRenderContext,
  ): number {
    const fontSize = 9;
    const bulletInset = 2;
    const textInset = 5;
    const lineHeight = this._lineHeight(fontSize);
    const align = this._getTextAlign(context.direction);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...BODY_COLOR);

    for (const item of block.items) {
      const lines = this._splitLines(pdf, item, context.contentWidth - textInset, fontSize);
      let firstLine = true;

      for (const line of lines) {
        y = this._ensureSpace(y, lineHeight + 0.5, context);
        if (firstLine) {
          pdf.setFillColor(...BRAND_COLOR);
          pdf.circle(this._getBulletX(context, bulletInset), y - 1.4, 0.7, 'F');
          firstLine = false;
        }
        pdf.text(line, this._getTextX(context, textInset), y, { align });
        y += lineHeight + 0.5;
      }

      y += 1;
    }

    return y + 1;
  }

  // HTML to blocks

  private _parseBlocks(html: string, title: string): PdfBlock[] {
    if (typeof DOMParser === 'undefined') return [];
    const doc    = new DOMParser().parseFromString(html, 'text/html');
    const blocks = this._extractBlocks(doc.body, /* topLevel */ true);
    // Drop the first heading if it duplicates the document title
    if (blocks[0]?.type === 'heading' && this._titlesMatch(blocks[0].text, title)) {
      blocks.shift();
    }
    return blocks;
  }

  private _extractBlocks(root: ParentNode, topLevel = false): PdfBlock[] {
    const blocks: PdfBlock[] = [];
    const children = Array.from(root.childNodes).filter((n): n is HTMLElement => n instanceof HTMLElement);
    children.forEach((node, i) => {
      blocks.push(...this._parseElement(node));
      // Mirror the browser's border-bottom section divider between top-level divs
      if (topLevel && node.tagName.toLowerCase() === 'div' && i < children.length - 1) {
        blocks.push({ type: 'divider' });
      }
    });
    return blocks;
  }

  private _parseElement(el: HTMLElement): PdfBlock[] {
    const headingBlock = this._getHeadingBlock(el);
    if (headingBlock) return [headingBlock];

    const paragraphBlock = this._getParagraphBlock(el);
    if (paragraphBlock) return [paragraphBlock];

    if (this._isListElement(el)) {
      return this._getListBlocks(el);
    }

    return this._extractBlocks(el);
  }

  private _getHeadingBlock(el: HTMLElement): PdfHeadingBlock | null {
    if (!el.matches('h5, p.text-decoration-underline.fw-bold')) {
      return null;
    }

    const text = this._norm(el.textContent || '');
    return text ? { type: 'heading', text } : null;
  }

  private _getParagraphBlock(el: HTMLElement): PdfParagraphBlock | null {
    if (el.tagName.toLowerCase() !== 'p') {
      return null;
    }

    const text = this._norm(el.textContent || '');
    if (!text) {
      return null;
    }

    return {
      type: 'paragraph',
      text,
      emphasis: el.classList.contains('text-dark') || el.classList.contains('text-black'),
    };
  }

  private _isListElement(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase();
    return tag === 'ul' || tag === 'ol';
  }

  private _getListBlocks(el: HTMLElement): PdfBlock[] {
    const items: string[] = [];
    const blocks: PdfBlock[] = [];

    for (const child of Array.from(el.children)) {
      if (!(child instanceof HTMLElement)) {
        continue;
      }
      this._collectListChildBlocks(child, items, blocks);
    }

    if (items.length) {
      blocks.push({ type: 'list', items });
    }

    return blocks;
  }

  private _collectListChildBlocks(child: HTMLElement, items: string[], blocks: PdfBlock[]): void {
    if (child.tagName.toLowerCase() === 'li') {
      const text = this._norm(child.textContent || '');
      if (text) {
        items.push(text);
      }
      return;
    }

    blocks.push(...this._parseElement(child));
  }

  // Utilities

  private _splitLines(pdf: jsPDF, text: string, maxWidth: number, fontSize: number): string[] {
    pdf.setFontSize(fontSize);
    const result = pdf.splitTextToSize(text, maxWidth);
    return Array.isArray(result) ? result : [result];
  }

  private _norm(s: string): string { return s.replaceAll(/\s+/g, ' ').trim(); }

  private _titlesMatch(a: string, b: string): boolean {
    const n = (v: string) => v.toLowerCase().replaceAll('&', 'and').replaceAll(/[^a-z0-9\u0600-\u06ff]+/g, '');
    return n(a) === n(b);
  }

  private _drawHeaderBadge(
    pdf: jsPDF,
    data: LegalDocumentPdfExportData,
    pw: number,
  ): void {
    const fontSize = 6.5;
    const badgePadX = 3;
    const badgeH = 6;
    const badgeY = (HEADER_H - badgeH) / 2;
    const badgeLabel = data.title;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    const badgeTw = pdf.getTextWidth(badgeLabel);
    const badgeW = badgePadX + badgeTw + badgePadX;
    const badgeX = pw - MARGIN_X - badgeW;

    // Badge background — rounded rect matching web
    pdf.setFillColor(255, 255, 255);
    this._setOpacity(pdf, 0.12);
    pdf.roundedRect(badgeX, badgeY, badgeW, badgeH, 2, 2, 'F');
    this._setOpacity(pdf, 1);

    // Badge text
    pdf.setTextColor(...WHITE);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    pdf.text(badgeLabel, badgeX + (badgeW / 2), badgeY + (badgeH / 2) + 0.8, { align: 'center' });
  }

  private _drawBrandLogo(
    pdf: jsPDF,
    logo: PdfImageData | null,
    barY: number,
    barHeight: number,
  ): void {
    if (logo) {
      pdf.addImage(logo.dataUrl, logo.format, MARGIN_X, barY, BRAND_LOGO_WIDTH, barHeight);
      return;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...WHITE);
    pdf.text('Deben', MARGIN_X, barY + (barHeight / 2) + 1);
  }

  private _getPageCount(pdf: jsPDF): number {
    return (pdf as jsPDF & PdfPageCountCapable).getNumberOfPages();
  }

  private _setOpacity(pdf: jsPDF, opacity: number): void {
    const advancedPdf = pdf as jsPDF & PdfGStateCapable;
    advancedPdf.setGState(advancedPdf.GState({ opacity }));
  }

  private _ensureSpace(y: number, needed: number, context: PdfRenderContext): number {
    return y + needed > context.contentBottom ? context.addPage() : y;
  }

  private _getTextAlign(direction: PdfDirection): PdfTextAlign {
    return direction === 'rtl' ? 'right' : 'left';
  }

  private _getTextX(context: PdfRenderContext, inset = 0): number {
    return context.direction === 'rtl' ? context.contentRight - inset : context.contentLeft + inset;
  }

  private _getBulletX(context: PdfRenderContext, inset: number): number {
    return context.direction === 'rtl' ? context.contentRight - inset : context.contentLeft + inset;
  }

  private _lineHeight(fontSize: number): number {
    return fontSize * 0.37;
  }

  private async _loadLogo(): Promise<PdfImageData | null> {
    for (const src of ['assets/images/logo-light.svg', 'assets/images/logo-light-sm.svg']) {
      try { return await this._fetchImage(src); } catch { /* try next */ }
    }
    return null;
  }

  private async _fetchImage(src: string): Promise<PdfImageData> {
    const blob = await firstValueFrom(this._commonService.downloadFile(src));
    const ext  = src.split('.').pop()?.toLowerCase() || '';
    if ((blob.type || '').includes('svg') || ext === 'svg') {
      return { dataUrl: await this._svgToPng(blob), format: 'PNG' };
    }
    const dataUrl = await this._blobToDataUrl(blob);
    return { dataUrl, format: dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG' };
  }

  private _blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload  = () => res(r.result as string);
      r.onerror = () => rej(new Error('FileReader failed'));
      r.readAsDataURL(blob);
    });
  }

  private async _svgToPng(blob: Blob): Promise<string> {
    const url = URL.createObjectURL(new Blob([await blob.text()], { type: 'image/svg+xml' }));
    return new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || img.width || 318;
        const h = img.naturalHeight || img.height || 100;
        const scale = 4;
        const sliceHeight = (w * HEADER_H) / BRAND_LOGO_WIDTH;
        const canvas = document.createElement('canvas');
        canvas.width = w * scale;
        canvas.height = Math.round(sliceHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); rej(new Error('No canvas context')); return; }
        ctx.fillStyle = BRAND_COLOR_CSS;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, (sliceHeight - h) / 2, w, h);
        URL.revokeObjectURL(url);
        res(canvas.toDataURL('image/png'));
      };
      img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('SVG load failed')); };
      img.src = url;
    });
  }
}
